import React, { MouseEventHandler } from "react";
import { PageSize } from "api/model/dashboard/DashboardReportOptions";
import { T } from "services/lang/LanguageService";
import { Instant } from "@js-joda/core";
import { Breakpoint, useBreakpoint } from "hooks/useGetBreakpojnts";
import useIsMobile from "hooks/useIsMobile";
import { DashboardLayoutType, IDashboardComponent } from "api/model/dashboard/DashboardComponent";
import DashboardComponent from "../../group-component/GroupComponent";
import { ContextMenuItem } from "../interfaces";
import "../Layout.scss";

interface HorizontalFlexLayoutProps {
  components: IDashboardComponent[];
  reportPageSize?: PageSize;
  tabId: number;
  align?: string;
  justify?: string;
  layoutType: DashboardLayoutType;
  groupId: string;
  refTime?: Instant;
  onContextMenu: MouseEventHandler<HTMLDivElement>;
  setCurrentContextMenuItems: (currentContextMenuItems: ContextMenuItem[]) => void;
}

const HorizontalFlexLayout = ({
  components,
  align,
  justify,
  reportPageSize,
  tabId,
  layoutType,
  groupId,
  refTime,
  onContextMenu,
  setCurrentContextMenuItems,
}: HorizontalFlexLayoutProps) => {
  const isMobile = useIsMobile();

  const breakpoint = useBreakpoint();

  const getComponentWidth = (component: IDashboardComponent): number | null => {
    if (isMobile || component.type === "SEPARATOR") {
      return 12;
    }

    const flexWidth = component.layout?.HORIZONTAL_FLEX?.width;
    const gridWidth = component.layout?.GRID?.width;

    if (flexWidth) {
      let widthAdjustment = 0;

      switch (breakpoint) {
        case Breakpoint.XS:
        case Breakpoint.SM:
        case Breakpoint.MD:
          widthAdjustment = 4;
          break;
        case Breakpoint.LG:
          widthAdjustment = 2;
          break;
        case Breakpoint.XL:
          break;
        default:
      }

      const adjustedWidth = flexWidth + widthAdjustment;
      return Math.min(adjustedWidth, 12);
    }

    if (gridWidth) {
      return gridWidth;
    }

    if (reportPageSize) {
      return null;
    }

    return 6;
  };

  const getComponentHeight = (component: IDashboardComponent) => {
    if (component.type === "NOT_SUPPORTED") {
      return "auto";
    }
    if (component.layout?.HORIZONTAL_FLEX?.height) {
      return component.layout.HORIZONTAL_FLEX.height;
    }
    if (component.layout?.GRID?.height) {
      return "100%";
    }
    return "max-content";
  };

  return (
    <div
      key={groupId}
      style={{ height: "100%" }}
      className={`w-full flex flex-row flex-wrap align-items-${align?.toLowerCase()} justify-content-${justify?.toLowerCase()}`}
    >
      {components?.length > 0 ? (
        components.map(component => (
          <div
            key={component.id}
            onContextMenu={onContextMenu}
            className={`w-${getComponentWidth(component)} layout-wrapper horizontal-layout ${reportPageSize && `border-1 border-round-sm`}`}
            style={{
              height: getComponentHeight(component),
              width: reportPageSize ? component.config.width : "auto",
            }}
          >
            <DashboardComponent
              groupId={groupId}
              tabId={tabId}
              component={component}
              layoutType={layoutType}
              setCurrentContextMenuItems={setCurrentContextMenuItems}
              isReports={!!reportPageSize}
              refTime={refTime}
            />
          </div>
        ))
      ) : (
        <div className="pl-3">{T("error.group-has-no-components")}</div>
      )}
    </div>
  );
};

export default HorizontalFlexLayout;
